import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Video, Plus, Users, X, Play, StopCircle, Trash2, Upload,
  Presentation as PresentIcon, ChevronLeft, ChevronRight, Loader2, AlertTriangle,
} from "lucide-react";
import Daily from "@daily-co/daily-js";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const EYE = "text-[10px] uppercase tracking-[0.15em] text-slate-500 font-mono";
const BASE = "/live-meetings";

export default function LiveMeetings() {
  const { user } = useAuth();
  const [health, setHealth] = useState({ enabled: true });
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [inCall, setInCall] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [h, m] = await Promise.all([api.get(`${BASE}/health`), api.get(BASE)]);
      setHealth(h.data);
      setMeetings(m.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="live-meetings-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className={EYE + " mb-1 flex items-center gap-2"}><Video className="w-3 h-3" /> LIVE VIDEO · PRESENTATIONS</div>
          <h1 className="font-display text-3xl text-white tracking-tight">Live Meetings</h1>
          <p className="text-slate-400 text-sm mt-1 max-w-2xl">
            Schedule live video calls with your team or clients. Upload a slide deck and drive it together, in real time.
          </p>
        </div>
        {health.enabled && (
          <button onClick={() => setScheduleOpen(true)} data-testid="schedule-btn"
            className="px-5 py-2.5 flex items-center gap-2 text-sm font-medium"
            style={{ background: "var(--gold-500)", color: "#050B14" }}>
            <Plus className="w-4 h-4" /> Schedule meeting
          </button>
        )}
      </div>

      {!health.enabled && (
        <div className="nexus-card p-5 flex items-start gap-3 border-amber-500/30" data-testid="disabled-notice">
          <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />
          <div className="text-sm text-slate-300 leading-relaxed">
            Live meetings are not yet activated on this workspace. An administrator must add <span className="font-mono text-[#D4AF37]">DAILY_API_KEY</span> to the server environment.
            <br />
            <span className="text-xs text-slate-500">Get one free at <a href="https://dashboard.daily.co" target="_blank" rel="noreferrer" className="text-[#D4AF37] underline">dashboard.daily.co</a> → Developers → copy the domain API key.</span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-10 flex items-center justify-center text-slate-500 font-mono text-xs">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> LOADING…
        </div>
      ) : meetings.length === 0 ? (
        <div className="nexus-card p-10 text-center" data-testid="live-empty">
          <Video className="w-10 h-10 mx-auto text-slate-600 mb-4" strokeWidth={1} />
          <div className="font-display text-xl text-white">No live meetings scheduled</div>
          <p className="text-sm text-slate-400 mt-1">Click "Schedule meeting" to create your first live session.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {meetings.map((m) => <MeetingCard key={m.id} meeting={m} onJoin={() => setInCall(m)} onRefresh={load} user={user} />)}
        </div>
      )}

      {scheduleOpen && <ScheduleModal onClose={() => setScheduleOpen(false)} onCreated={() => { setScheduleOpen(false); load(); }} />}
      {inCall && <MeetingRoom meeting={inCall} onLeave={() => setInCall(null)} user={user} />}
    </div>
  );
}

function MeetingCard({ meeting, onJoin, onRefresh, user }) {
  const starts = new Date(meeting.starts_at);
  const ends = new Date(meeting.ends_at);
  const now = new Date();
  const isOwner = meeting.owner_id === user?.id || user?.role === "admin" || user?.is_org_admin;
  const isLive = now >= new Date(starts.getTime() - 10 * 60000) && now <= ends && meeting.status !== "closed";
  const isPast = now > ends || meeting.status === "closed";

  const remove = async () => {
    if (!window.confirm(`Delete meeting "${meeting.title}"?`)) return;
    try { await api.delete(`${BASE}/${meeting.id}`); onRefresh(); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="nexus-card p-5 flex flex-col gap-3" data-testid={`live-${meeting.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-white font-medium truncate">{meeting.title}</div>
          <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
            {starts.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
          </div>
        </div>
        <span className="font-mono text-[10px] tracking-widest px-2 py-0.5 border" style={{
          borderColor: isLive ? "#22C55E" : isPast ? "#64748B" : "#D4AF37",
          color: isLive ? "#22C55E" : isPast ? "#64748B" : "#D4AF37",
        }}>{isLive ? "LIVE" : isPast ? "PAST" : "UPCOMING"}</span>
      </div>
      <div className="text-[11px] text-slate-500 flex items-center gap-3">
        <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {meeting.max_participants || 50}</span>
        {meeting.deck && <span className="flex items-center gap-1 text-[#D4AF37]"><PresentIcon className="w-3 h-3" /> deck</span>}
      </div>
      <div className="mt-auto pt-3 flex items-center gap-2">
        <button onClick={onJoin} disabled={isPast} data-testid={`join-${meeting.id}`}
          className="flex-1 px-3 py-2 text-xs font-medium flex items-center justify-center gap-1.5 disabled:opacity-40"
          style={{ background: isLive ? "#22C55E" : "var(--gold-500)", color: "#050B14" }}>
          <Play className="w-3 h-3" /> {isLive ? "JOIN NOW" : "Open room"}
        </button>
        {isOwner && !isPast && (
          <button onClick={remove} data-testid={`del-${meeting.id}`} className="p-2 border border-white/10 text-slate-400 hover:border-red-500/40 hover:text-red-400">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function ScheduleModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "",
    starts_at: new Date(Date.now() + 15 * 60000).toISOString().slice(0, 16),
    duration_minutes: 60, max_participants: 50,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(BASE, {
        ...form,
        starts_at: new Date(form.starts_at).toISOString(),
        duration_minutes: Number(form.duration_minutes),
        max_participants: Number(form.max_participants),
      });
      toast.success("Meeting scheduled");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="bg-[#0A111F] border border-white/10 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="schedule-modal">
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div>
            <div className={EYE}>// SCHEDULE</div>
            <h2 className="font-display text-xl text-white mt-1">New live meeting</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className={EYE + " mb-1.5 block"}>Meeting title</label>
            <input required data-testid="s-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="pr-input" placeholder="e.g. Investor Committee — Q4 review" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={EYE + " mb-1.5 block"}>Start</label>
              <input required type="datetime-local" data-testid="s-start" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="pr-input" />
            </div>
            <div>
              <label className={EYE + " mb-1.5 block"}>Duration (min)</label>
              <input required type="number" min={5} max={1440} data-testid="s-dur" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="pr-input" />
            </div>
          </div>
          <div>
            <label className={EYE + " mb-1.5 block"}>Max participants</label>
            <input type="number" min={2} max={200} value={form.max_participants} onChange={(e) => setForm({ ...form, max_participants: e.target.value })} className="pr-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={busy} data-testid="s-submit" className="px-5 py-2 flex items-center gap-2 text-sm font-medium disabled:opacity-40" style={{ background: "var(--gold-500)", color: "#050B14" }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
            </button>
          </div>
        </form>
        <style>{`.pr-input { width:100%; padding:0.55rem 0.75rem; background:#050B14; border:1px solid rgba(255,255,255,0.1); color:#F8FAFC; font-family:'IBM Plex Mono', monospace; font-size:0.875rem; outline:none; } .pr-input:focus { border-color:#D4AF37; }`}</style>
      </motion.div>
    </div>
  );
}

function MeetingRoom({ meeting, onLeave, user }) {
  const hostRef = useRef(null);
  const callRef = useRef(null);
  const [status, setStatus] = useState("Requesting token…");
  const [isOwner, setIsOwner] = useState(false);
  const [slidesOpen, setSlidesOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`${BASE}/${meeting.id}/token`);
        if (cancelled || !hostRef.current) return;
        setIsOwner(!!data.is_owner);
        const call = Daily.createFrame(hostRef.current, {
          iframeStyle: { width: "100%", height: "100%", border: "0", borderRadius: "8px" },
          showLeaveButton: true, showFullscreenButton: true,
        });
        callRef.current = call;
        call.on("joining-meeting", () => setStatus("Joining…"));
        call.on("joined-meeting", () => setStatus("Connected"));
        call.on("left-meeting", () => onLeave());
        call.on("error", (e) => { setStatus("Error"); toast.error(e?.error?.msg || "Meeting error"); });
        await call.join({ url: data.room_url, token: data.token });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not join");
        onLeave();
      }
    })();
    return () => {
      cancelled = true;
      const c = callRef.current;
      callRef.current = null;
      if (c) { c.leave().catch(() => {}); c.destroy().catch(() => {}); }
    };
  }, [meeting.id]);

  return (
    <div className="fixed inset-0 z-50 bg-[#050810] flex flex-col" data-testid="room">
      <div className="p-3 flex items-center justify-between border-b border-white/10 shrink-0">
        <div>
          <div className={EYE}>// LIVE · {status}</div>
          <div className="text-white text-sm font-medium">{meeting.title}</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setSlidesOpen(!slidesOpen)} data-testid="toggle-slides"
            className="px-3 py-1.5 text-xs font-mono tracking-widest border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/8">
            <PresentIcon className="w-3 h-3 inline mr-1" /> {slidesOpen ? "HIDE SLIDES" : "SLIDES"}
          </button>
          <button onClick={onLeave} data-testid="leave" className="px-3 py-1.5 text-xs font-mono tracking-widest border border-red-500/40 text-red-400 hover:bg-red-500/8">
            <StopCircle className="w-3 h-3 inline mr-1" /> LEAVE
          </button>
        </div>
      </div>
      <div className="flex-1 flex overflow-hidden">
        <div ref={hostRef} className={`${slidesOpen ? "w-1/2" : "w-full"} h-full p-3`} />
        {slidesOpen && (
          <div className="w-1/2 border-l border-white/10 p-3 overflow-hidden">
            <SlidesPanel meetingId={meeting.id} isOwner={isOwner} initialDeck={meeting.deck} />
          </div>
        )}
      </div>
    </div>
  );
}

function SlidesPanel({ meetingId, isOwner, initialDeck }) {
  const [deck, setDeck] = useState(initialDeck);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [slide, setSlide] = useState(0);
  const [uploading, setUploading] = useState(false);
  const pollRef = useRef(null);

  const loadDeck = async () => {
    try {
      const res = await api.get(`${BASE}/${meetingId}/deck/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      setPdfUrl(url);
    } catch { /* no deck yet */ }
  };

  useEffect(() => {
    if (deck) loadDeck();
    return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
  }, [deck?.file_name]);

  useEffect(() => {
    if (isOwner) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`${BASE}/${meetingId}/deck/state`);
        setSlide(data.slide_index || 0);
      } catch { /* silent */ }
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [isOwner, meetingId]);

  const pushSlide = async (newIdx) => {
    setSlide(newIdx);
    if (isOwner) {
      try { await api.post(`${BASE}/${meetingId}/deck/state`, { slide_index: newIdx }); }
      catch { /* silent */ }
    }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post(`${BASE}/${meetingId}/deck`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDeck({ file_name: data.file_name, size: data.size });
      toast.success("Deck uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  if (!deck && !isOwner) {
    return <div className="h-full flex flex-col items-center justify-center text-center">
      <PresentIcon className="w-8 h-8 text-slate-600 mb-3" strokeWidth={1.2} />
      <div className="text-slate-400 text-sm">Waiting for presenter to upload slides…</div>
    </div>;
  }
  if (!deck) {
    return <div className="h-full flex flex-col items-center justify-center gap-3">
      <PresentIcon className="w-8 h-8 text-slate-500" strokeWidth={1.2} />
      <div className="text-white font-medium">Upload a slide deck (PDF)</div>
      <label className="cursor-pointer px-4 py-2 text-xs font-mono tracking-widest border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/8" data-testid="upload-deck">
        <Upload className="w-3 h-3 inline mr-1" /> {uploading ? "UPLOADING…" : "CHOOSE PDF"}
        <input type="file" accept="application/pdf" onChange={upload} className="hidden" />
      </label>
    </div>;
  }
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-2 shrink-0">
        <div className={EYE}>// {deck.file_name} · slide {slide + 1}</div>
        {isOwner && (
          <div className="flex items-center gap-1">
            <button onClick={() => pushSlide(Math.max(0, slide - 1))} data-testid="slide-prev" className="p-1.5 border border-white/10 hover:border-[#D4AF37]/40">
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => pushSlide(slide + 1)} data-testid="slide-next" className="p-1.5 border border-white/10 hover:border-[#D4AF37]/40">
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      <div className="flex-1 bg-white overflow-hidden">
        {pdfUrl && <iframe title="slides" src={`${pdfUrl}#page=${slide + 1}&toolbar=0&navpanes=0`} className="w-full h-full border-0" />}
      </div>
      {!isOwner && <div className="mt-1 text-[10px] text-slate-500 font-mono">FOLLOWING PRESENTER · auto-syncs every 2s</div>}
    </div>
  );
}
